/**
 * One refresh for a burst of pushed alarm events, not one per event.
 *
 * WHY. The alarm stream (`events.ts`) used to invalidate every alarm query on every event. A
 * payload that moves N alarms is N events in the same instant, and every mounted alarm query
 * (the Command Centre's list and summary, the rail's summary, the Alarms board's pages, an open
 * triage drawer) refetched N times. Measured on the local Cratus stack on 2026-10-04: hundreds of
 * `429 Too Many Requests` a minute on `/alarms` and `/alarms/summary` for one person, which then
 * also starved every other screen of its budget.
 *
 * WHAT. The first event of a burst starts a short wait. Each further event moves it to `quietMs`
 * after itself, but never beyond `maxWaitMs` after the first — so a steady trickle still refreshes
 * at least that often and a board never lags a live fleet by more. When the wait ends while the
 * API has rate-limited this client (`backoff.ts`), the refresh waits that window out instead of
 * adding to it.
 *
 * Pure apart from the clock, which is injected so the timing is tested exactly.
 */

export interface CoalescerClock {
  now: () => number;
  set: (fn: () => void, ms: number) => unknown;
  clear: (handle: unknown) => void;
}

const REAL_CLOCK: CoalescerClock = {
  now: () => Date.now(),
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface Coalescer {
  /** An event arrived: refresh soon, once for the whole burst. */
  poke: () => void;
  /** Forget a pending refresh (the stream closed). */
  cancel: () => void;
}

export function createRefreshCoalescer(
  run: () => void,
  options: {
    quietMs: number;
    maxWaitMs: number;
    /** Ms to hold back before running, e.g. the rate-limit window left; 0 to run now. */
    holdFor?: () => number;
    clock?: CoalescerClock;
  },
): Coalescer {
  const { quietMs, maxWaitMs, holdFor = () => 0, clock = REAL_CLOCK } = options;
  let timer: unknown = null;
  let firstAt: number | null = null;

  const fire = (): void => {
    timer = null;
    const hold = holdFor();
    if (hold > 0) {
      /* Still in the burst: the events that arrive meanwhile join this refresh. */
      timer = clock.set(fire, hold);
      return;
    }
    firstAt = null;
    run();
  };

  return {
    poke: () => {
      const now = clock.now();
      if (firstAt === null) firstAt = now;
      const due = Math.min(now + quietMs, firstAt + maxWaitMs);
      if (timer !== null) clock.clear(timer);
      timer = clock.set(fire, Math.max(0, due - now));
    },
    cancel: () => {
      if (timer !== null) clock.clear(timer);
      timer = null;
      firstAt = null;
    },
  };
}
